import * as React from 'react';
import PropTypes from 'prop-types';
import { useCallback, useContext } from 'react';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import SvgIcon from '@mui/material/SvgIcon';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import useMediaQuery from '@mui/material/useMediaQuery';
import DataList from './DataList';
import MoreVert from "@mui/icons-material/MoreVert";
import { usePopover } from '../hooks/usePopover';
import { ProjectPopover } from './ProjectPopover';
import { useTranslation } from "react-i18next";
import { AuthContext } from "../context/AuthContext";
import { formatSizeUnits } from '../utils/fileManagement';
import UploadIcon from '@mui/icons-material/Upload';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import StorageIcon from '@mui/icons-material/Storage';

const ProjectDashboard = ({
  handleClickOnNewData,
  handleClickOnEditProject,
  handleClickOnDeleteProject,
  projectSelected,
  reloadProjects,
  processings
}) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 600px)');

  const { service } = useContext(AuthContext);

  const getFormattedDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString();
}

  //useEffect(() => {
  //}, [projectSelected]);

  const handleNewDataClick = useCallback(() => {
    handleClickOnNewData();
  }, [handleClickOnNewData]);

  const projectPopover = usePopover();

  const files = projectSelected.attributes.data.data;
  const totalSize = files.reduce((acc, f) => acc + Number(f.attributes.size || 0), 0);

  return (
    <div>
      <Stack spacing={3}>
        <Grid
          size={12}
          container
          sx={{
            fontSize: '12px',
            paddingRight: 2,
            paddingTop: 2,
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: { xs: 'row', sm: 'row' },
          }}
        >
        <Grid>
        <Box sx={{ paddingLeft:2 }}>
        <Stack
          spacing={1}        
        >      
          <Stack
            direction="row"
            spacing={0}
            sx={{ justifyContent: 'flex-start', alignItems: 'center' }}
          >
            <Typography variant="h4">
              {projectSelected.attributes.title}
            </Typography>
            <IconButton onClick={projectPopover.handleOpen} ref={projectPopover.anchorRef}>
              <MoreVert fontSize="large" />
            </IconButton>
          </Stack>
          <Typography
            color="text.secondary"
            variant="h5"
          >
            {projectSelected.attributes.description}
          </Typography>
          {isMobile ? (
            <Stack spacing={0.5} sx={{ paddingTop: 1 }}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <SvgIcon color="action" fontSize="small"><InsertDriveFileIcon /></SvgIcon>
                <Typography variant="body2" color="text.secondary">
                  {files.length} {t('Files')}
                </Typography>
                {totalSize > 0 && (
                  <>
                    <SvgIcon color="action" fontSize="small"><StorageIcon /></SvgIcon>
                    <Typography variant="body2" color="text.secondary">
                      {formatSizeUnits(totalSize)}
                    </Typography>
                  </>
                )}
              </Stack>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <SvgIcon color="action" fontSize="small"><AddCircleOutlineIcon /></SvgIcon>
                <Typography variant="body2" color="text.secondary">
                  {t('CreatedOn')}{getFormattedDate(projectSelected.attributes.createdAt)}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <SvgIcon color="action" fontSize="small"><RefreshIcon /></SvgIcon>
                <Typography variant="body2" color="text.secondary">
                  {t('UpdatedOn')}{getFormattedDate(projectSelected.attributes.updatedAt)}
                </Typography>
              </Stack>
            </Stack>
          ) : (
            <Stack
              direction="row"
              spacing={1}
              sx={{ paddingTop: 1, justifyContent: 'flex-start', alignItems: 'center', flexWrap: 'wrap' }}
            >
              <SvgIcon color="action" fontSize="small"><InsertDriveFileIcon /></SvgIcon>
              <Typography variant="body2" color="text.secondary">
                {files.length} {t('Files')}
              </Typography>
              {totalSize > 0 && (
                <>
                  <SvgIcon color="action" fontSize="small"><StorageIcon /></SvgIcon>
                  <Typography variant="body2" color="text.secondary">
                    {formatSizeUnits(totalSize)}
                  </Typography>
                </>
              )}
              <SvgIcon color="action" fontSize="small"><AddCircleOutlineIcon /></SvgIcon>
              <Typography variant="body2" color="text.secondary">
                {t('CreatedOn')}{getFormattedDate(projectSelected.attributes.createdAt)}
              </Typography>
              <SvgIcon color="action" fontSize="small"><RefreshIcon /></SvgIcon>
              <Typography variant="body2" color="text.secondary">
                {t('UpdatedOn')}{getFormattedDate(projectSelected.attributes.updatedAt)}
              </Typography>
            </Stack>
          )}
        </Stack>
        </Box>
        </Grid>
        <Grid sx={{ pt: { xs: 1.5, sm: 0 }, pl: { xs: 2, sm: 0 } }}>
          <Button
            variant="contained"
            size="large"
            startIcon={(
              <SvgIcon fontSize="medium">
                <UploadIcon />
              </SvgIcon>
            )}
            onClick={(event) => handleNewDataClick(event)}
          >
            {t("UploadData")}
          </Button>
        </Grid>
        </Grid>
        <DataList data={projectSelected.attributes.data.data} reloadProjects={reloadProjects} processings={processings}/>
      </Stack>

      <ProjectPopover
        anchorEl={projectPopover.anchorRef.current}
        open={projectPopover.open}
        onClose={projectPopover.handleClose}
        handleClickOnUploadProject={handleClickOnNewData}
        handleClickOnEditProject={handleClickOnEditProject}
        handleClickOnDeleteProject={handleClickOnDeleteProject}
      />
    </div>
  );
}

ProjectDashboard.propTypes = {
  handleClickOnNewData: PropTypes.func.isRequired,
  handleClickOnEditProject: PropTypes.func.isRequired,
  handleClickOnDeleteProject: PropTypes.func.isRequired,
  projectSelected: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    attributes: PropTypes.shape({
      title: PropTypes.string.isRequired,
      description: PropTypes.string,
      updatedAt: PropTypes.string,
      data: PropTypes.shape({
        data: PropTypes.arrayOf(PropTypes.object)
      })
    })
  }),
  reloadProjects: PropTypes.func.isRequired,
  processings: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    description: PropTypes.string,
    url: PropTypes.string,
    output: PropTypes.string,
    subProcessings: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      title: PropTypes.string,
      description: PropTypes.string,
    }))
  }))
};

export default React.memo(ProjectDashboard);